// Global flag to ensure Swym is only initialized once
if (!window.swymWishlistInitialized) {
  window.swymWishlistInitialized = false;
  window.swymWishlistButtons = [];
}

export default (node) => {
  // Get button elements
  const button = node.querySelector('[data-wishlist-button]');
  const unfilledIcon = node.querySelector('[data-unfilled-icon]');
  const filledIcon = node.querySelector('[data-filled-icon]');

  if (!button) return;

  // Helper to parse boolean strings
  const parseBool = (val) => val === 'true' || val === true;

  // Helper to parse array from string
  const parseArray = (val, separator = ',') => {
    if (!val) return [];
    return val.split(separator).filter(Boolean);
  };

  // Helper to parse JSON safely
  const parseJSON = (val) => {
    if (!val) return null;
    try {
      return JSON.parse(val);
    } catch (e) {
      return null;
    }
  };

  const productData = {
    epi: parseInt(button.dataset.variantId),
    empi: parseInt(button.dataset.productId),
    du: button.dataset.productUrl,
    dt: button.dataset.productTitle,
    pr: parseFloat(button.dataset.variantPrice),
    iu: button.dataset.productImage,
    cprops: {
      // Product-level data
      handle: button.dataset.productHandle,
      type: button.dataset.productType,
      tags: parseArray(button.dataset.productTags),
      price_varies: parseBool(button.dataset.priceVaries),
      certified: parseBool(button.dataset.certified),
      certified_reason: button.dataset.certifiedReason,
      related_products: parseJSON(button.dataset.relatedProductsJson) || [],
      images: parseArray(button.dataset.productImages, '|'),

      // Variant-level data
      available: parseBool(button.dataset.variantAvailable),
      inventory_quantity: parseInt(button.dataset.variantInventory) || 0,
      compare_at_price: parseFloat(button.dataset.compareAtPrice) || 0,

      // Variant metafields
      is_double_width: parseBool(button.dataset.isDoubleWidth),
      video_enabled: parseBool(button.dataset.videoEnabled),
      mood_video: button.dataset.moodVideo,
      web_color_hex: button.dataset.webColorHex,
      web_color_name: button.dataset.webColorName,
      secondary_color_hex: button.dataset.secondaryColorHex,
      secondary_color_name: button.dataset.secondaryColorName,
      out_of_stock_strategy: button.dataset.outOfStockStrategy,
    },
  };

  // Store button instance globally
  const buttonInstance = {
    node,
    button,
    unfilledIcon,
    filledIcon,
    productData,
  };
  window.swymWishlistButtons.push(buttonInstance);

  // Initialize Swym integration (only once globally)
  const initSwymGlobal = (swat) => {
    if (window.swymWishlistInitialized) return;
    window.swymWishlistInitialized = true;

    fetchList(swat).then(() => {
      if (!window.swymSelectedListId) {
        createList(swat);
      }
      addSwymEventListenerGlobal(swat);
      updateAllButtonStates();
    });
  };

  // Create a new wishlist if it doesn't exist
  const createList = (swat) => {
    const listConfig = { lname: 'My Wishlist' };

    const onSuccess = ({ lid }) => {
      window.swymSelectedListId = lid;
      updateAllButtonStates();
    };

    const onError = (error) => {};

    swat.createList(listConfig, onSuccess, onError);
  };

  // Fetch the wishlist data
  const fetchList = (swat) => {
    return new Promise((resolve, reject) => {
      const onSuccess = (lists) => {
        if (lists && lists[0]) {
        }
        window.swymWishLists = lists;
        window.swymSelectedListId = lists && lists[0] && lists[0].lid;
        resolve(lists);
      };

      const onError = (error) => {
        reject(error);
      };

      if (!window.swymWishLists) {
        swat.fetchLists({
          callbackFn: onSuccess,
          errorFn: onError,
        });
      } else {
        resolve(window.swymWishLists);
      }
    });
  };

  // Refresh the wishlist data
  const refreshList = (swat, delay = 0) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        window.swymWishLists = null;
        fetchList(swat).then(resolve);
      }, delay);
    });
  };

  // Add product to wishlist
  const addToWishlist = (swat, product) => {
    const onSuccess = (addedListItem) => {
      // Verify the item was actually added by fetching fresh data
      setTimeout(() => {
        window.swymWishLists = null;
        fetchList(swat).then((lists) => {
          updateAllButtonStates();
        });
      }, 1000);
    };

    const onError = (error) => {
      swat.ui.showErrorNotification({
        message: 'Error adding product to wishlist',
      });
    };

    const lid = window.swymSelectedListId;
    swat.addToList(lid, product, onSuccess, onError);
  };

  // Remove product from wishlist
  const removeFromWishlist = (swat, product) => {
    const onSuccess = (deletedProduct) => {
      updateAllButtonStates();
    };

    const onError = (error) => {
      swat.ui.showErrorNotification({
        message: 'Error removing product from wishlist',
      });
    };

    const lid = window.swymSelectedListId;
    swat.deleteFromList(lid, product, onSuccess, onError);
  };

  // Update a specific button's state
  const updateSingleButtonState = (btnInstance) => {
    const {
      unfilledIcon: unfilled,
      filledIcon: filled,
      button: btn,
      productData: data,
    } = btnInstance;

    if (!window.swymWishLists || !unfilled || !filled) return;

    const labelAdd = btn.dataset.labelAdd || 'Add to wishlist';
    const labelRemove = btn.dataset.labelRemove || 'Remove from wishlist';

    let isWishlisted = false;

    window.swymWishLists.forEach((list) => {
      list.listcontents.forEach((item) => {
        if (item.empi == data.empi && item.epi == data.epi) {
          isWishlisted = true;
        }
      });
    });

    // Update UI
    if (isWishlisted) {
      unfilled.classList.add('hidden');
      filled.classList.remove('hidden');
      btn.setAttribute('aria-label', labelRemove);
      btn.classList.add('wishlisted');
    } else {
      unfilled.classList.remove('hidden');
      filled.classList.add('hidden');
      btn.setAttribute('aria-label', labelAdd);
      btn.classList.remove('wishlisted');
    }
  };

  // Update all buttons on the page
  const updateAllButtonStates = () => {
    if (!window.swymWishLists) return;

    window.swymWishlistButtons.forEach((btnInstance) => {
      updateSingleButtonState(btnInstance);
    });
  };

  // Show custom notification
  const showCustomNotification = (swat, image, title, action) => {
    const successMessage = `
      <style>
        .swym-notification-success-inner .swym-image { display: none !important }
        #swym-custom-notification-content { display: flex; gap: 10px; align-items: center; cursor: pointer }
        #swym-custom-notification-content #swym-custom-image-container { min-height: 50px; min-width: 50px; max-height: 50px; max-width: 50px }
        #swym-custom-notification-content #swym-custom-image-container #swym-custom-image { width: 100%; height: 100%; object-fit: cover; border-radius: 50% }
        #swym-custom-notification-content #swym-custom-title-container { font-weight: 500; font-size: 14px }
      </style>
      <div id="swym-custom-notification-content" onclick="_swat.ui.open()">
        <div id="swym-custom-image-container">
          <img id="swym-custom-image" src="${image}" alt="wishlisted product image"/>
        </div>
        <div id="swym-custom-title-container">
          <strong>${title}</strong> has been ${action === 'remove' ? 'removed from' : 'added to'} your wishlist
        </div>
      </div>
    `;
    swat.ui.showSuccessNotification({ message: successMessage });
  };

  // Add Swym event listeners (only called once globally)
  const addSwymEventListenerGlobal = (swat) => {
    swat.evtLayer.addEventListener(
      swat.JSEvents.removedFromWishlist,
      (event) => {
        const { iu: image, dt: title } = event.detail.d;
        showCustomNotification(swat, image, title, 'remove');
        // Add delay to allow Swym API to update
        refreshList(swat, 500).then(() => updateAllButtonStates());
      }
    );

    swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, (event) => {
      const { iu: image, dt: title } = event.detail.d;
      showCustomNotification(swat, image, title, 'add');
      // Add delay to allow Swym API to update
      refreshList(swat, 500).then(() => updateAllButtonStates());
    });

    swat.evtLayer.addEventListener(swat.JSEvents.variantChanged, (data) => {
      const currentVariant = data.detail.d.variant.id;
      const currentProductId = data.detail.d.product.id;

      // Update all buttons for this product
      window.swymWishlistButtons.forEach((btnInstance) => {
        if (currentProductId === btnInstance.productData.empi) {
          btnInstance.productData.epi = currentVariant;
          btnInstance.button.dataset.variantId = currentVariant;
        }
      });

      setTimeout(updateAllButtonStates, 200);
    });
  };

  // Handle button click
  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window._swat) {
      return;
    }

    const swat = window._swat;
    const isWishlisted = button.classList.contains('wishlisted');

    if (isWishlisted) {
      removeFromWishlist(swat, productData);
    } else {
      addToWishlist(swat, productData);
    }
  };

  // Initialize
  const init = () => {
    button.addEventListener('click', handleButtonClick);

    // Initialize Swym when ready (only once globally)
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = [];
    }

    // Check if Swym is already loaded
    if (window._swat) {
      initSwymGlobal(window._swat);
      // Update this specific button
      updateSingleButtonState(buttonInstance);
    } else {
      window.SwymCallbacks.push(initSwymGlobal);
    }
  };

  init();
};
